import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Mail, Lock, AlertCircle, Globe } from 'lucide-react';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  // Redirect to dashboard if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/');
    }
  }, [isAuthenticated, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const result = await login(email, password);

    if (result.success) {
      navigate('/');
    } else {
      setError(result.error);
    }

    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-[hsl(220_6%_6%)] flex">
      {/* Left Panel - Branding */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#00B8B8]/10 via-transparent to-[#0EA5E9]/10"></div>
        <div className="absolute inset-0" style={{backgroundImage: 'url(https://images.pexels.com/photos/7130565/pexels-photo-7130565.jpeg)', backgroundSize: 'cover', backgroundPosition: 'center', opacity: 0.15}}></div>
        <div className="relative z-10 flex flex-col justify-center items-center w-full p-12">
          <div className="w-24 h-24 bg-white rounded-2xl flex items-center justify-center mb-8 shadow-[0_0_40px_rgba(0,184,184,0.4)] overflow-hidden p-2">
            <img src="/danwei-logo.png" alt="Danwei HR" className="w-full h-full object-contain" />
          </div>
          <h1 className="text-5xl font-bold text-white mb-4 tracking-tight">Danwei HR</h1>
          <p className="text-xl text-[#0EA5E9] mb-8">Human Resource Suite</p>
          <p className="text-[hsl(215_10%_70%)] text-center max-w-md text-lg leading-relaxed">
            Enterprise HR Management powered by intelligent automation. 
            Streamline payroll, attendance, and workforce management.
          </p>
          <div className="mt-12 flex items-center gap-8">
            <div className="text-center">
              <p className="text-3xl font-bold text-[#00B8B8]">24/7</p>
              <p className="text-sm text-[hsl(215_10%_70%)]">Availability</p>
            </div>
            <div className="w-px h-12 bg-[hsl(215_10%_22%)]"></div>
            <div className="text-center">
              <p className="text-3xl font-bold text-[#00B8B8]">100%</p>
              <p className="text-sm text-[hsl(215_10%_70%)]">Secure</p>
            </div>
            <div className="w-px h-12 bg-[hsl(215_10%_22%)]"></div>
            <div className="text-center">
              <p className="text-3xl font-bold text-[#00B8B8]">AI</p>
              <p className="text-sm text-[hsl(215_10%_70%)]">Powered</p>
            </div>
          </div>
        </div>
      </div>

      {/* Right Panel - Login Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-6">
        <div className="w-full max-w-md">
          <div className="bg-[hsl(220_6%_8%)] border border-[hsl(215_10%_22%)] rounded-2xl p-8 shadow-[0_12px_40px_rgba(0,0,0,0.50)]">
            {/* Mobile Logo */}
            <div className="lg:hidden text-center mb-8">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-white rounded-xl mb-4 overflow-hidden p-1">
                <img src="/danwei-logo.png" alt="Danwei HR" className="w-full h-full object-contain" />
              </div>
              <h1 className="text-2xl font-bold text-white">Danwei HR</h1>
              <p className="text-sm text-[#0EA5E9]">Human Resource Suite</p>
            </div>

            <div className="hidden lg:block mb-8">
              <h2 className="text-2xl font-bold text-white">Welcome back</h2>
              <p className="text-[hsl(215_10%_70%)] mt-2">Sign in to access your HR dashboard</p>
            </div>

            {error && (
              <div className="mb-6 p-4 bg-[#3B1E21] border border-[#5C2A2E] rounded-lg flex items-center gap-3 text-[#FFB3B8]">
                <AlertCircle size={20} />
                <span className="text-sm">{error}</span>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-[hsl(210_15%_92%)] mb-2">Email</label>
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 transform -translate-y-1/2 text-[hsl(215_10%_50%)]" size={20} />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    data-testid="login-email-input"
                    className="w-full pl-12 pr-4 py-3 bg-[hsl(220_6%_12%)] border border-[hsl(215_10%_22%)] rounded-lg text-[hsl(210_15%_92%)] placeholder-[hsl(215_10%_50%)] focus:border-[#00B8B8] focus:ring-2 focus:ring-[#00B8B8]/20 transition-colors duration-150"
                    placeholder="admin@danweihr.com"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-[hsl(210_15%_92%)] mb-2">Password</label>
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 transform -translate-y-1/2 text-[hsl(215_10%_50%)]" size={20} />
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    data-testid="login-password-input"
                    className="w-full pl-12 pr-4 py-3 bg-[hsl(220_6%_12%)] border border-[hsl(215_10%_22%)] rounded-lg text-[hsl(210_15%_92%)] placeholder-[hsl(215_10%_50%)] focus:border-[#00B8B8] focus:ring-2 focus:ring-[#00B8B8]/20 transition-colors duration-150"
                    placeholder="Enter your password"
                    required
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                data-testid="login-submit-button"
                className="w-full bg-gradient-to-r from-[#00B8B8] to-[#0EA5E9] text-white py-3 rounded-lg font-semibold hover:shadow-[0_0_20px_rgba(0,184,184,0.4)] transition-shadow duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Signing in...' : 'Sign In'}
              </button>
            </form>

            <div className="mt-8 pt-6 border-t border-[hsl(215_10%_22%)]">
              <p className="text-center text-sm text-[hsl(215_10%_70%)]">
                Powered by <span className="text-[#00B8B8]">Danwei HR</span>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;